import { useState } from "react";
import { useAudit, useMe, usePlatformStatus } from "../../api/hooks";
import { Split } from "../../components/Split";
import { Empty, JsonTree, Spinner, errText, fmtDate } from "../../components/ui";
import "./platform.css";

/** Audit trail of the active tenant plus which platform services are wired up. */
export function AdminPage() {
  const me = useMe();
  const status = usePlatformStatus();
  const [action, setAction] = useState("");
  const [user, setUser] = useState("");
  const [hours, setHours] = useState<number>(24 * 7);
  const [allTenants, setAll] = useState(false);
  const audit = useAudit({ action: action || undefined, user: user || undefined, hours, allTenants, limit: 500 });
  const [open, setOpen] = useState<number | null>(null);

  if (!me.data) return <Spinner label="loading" />;
  return (
    <Split id="admin" className="admin-page" sizes={["1fr", 320]} minSizes={[360, 220]}>
      <div className="pane">
        <div className="pane-head row wrap">
          Audit log
          <span className="dim small">{allTenants ? "all operators" : me.data.tenant}</span>
          <span className="spacer" />
          <input className="inp sm" placeholder="action (e.g. run.submit)" value={action} onChange={(e) => setAction(e.target.value)} />
          <input className="inp sm" placeholder="user" value={user} onChange={(e) => setUser(e.target.value)} />
          <select className="inp sm" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            <option value={1}>last hour</option>
            <option value={24}>last day</option>
            <option value={24 * 7}>last week</option>
            <option value={24 * 30}>last 30 days</option>
            <option value={24 * 365}>last year</option>
          </select>
          {me.data.is_admin ? (
            <label className="row small" style={{ gap: 4 }}>
              <input type="checkbox" checked={allTenants} onChange={(e) => setAll(e.target.checked)} /> all operators
            </label>
          ) : null}
          {audit.isFetching ? <Spinner /> : null}
        </div>
        <div className="pane-body" style={{ overflow: "auto" }}>
          {audit.isError ? <div className="problem err">{errText(audit.error)}</div> : null}
          {audit.data && !audit.data.entries.length ? <Empty title="No audit entries" /> : null}
          {audit.data?.entries.length ? (
            <table className="tbl">
              <thead>
                <tr>
                  <th>At</th>
                  {allTenants ? <th>Operator</th> : null}
                  <th>User</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Status</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {audit.data.entries.map((e) => (
                  <tr key={e.id} className={`clickable${open === e.id ? " sel" : ""}`} onClick={() => setOpen(open === e.id ? null : e.id)}>
                    <td className="small dim" style={{ whiteSpace: "nowrap" }}>
                      {fmtDate(e.at)}
                    </td>
                    {allTenants ? <td className="mono small">{e.operator_id}</td> : null}
                    <td>{e.user_name || e.user_id}</td>
                    <td className="mono">{e.action}</td>
                    <td className="mono small">{e.target}</td>
                    <td className="num">{e.status ?? ""}</td>
                    <td className="small dim" style={{ maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: open === e.id ? "normal" : "nowrap" }}>
                      {open === e.id ? <JsonTree value={e.detail} /> : summarise(e.detail)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      </div>
      <div className="pane">
        <div className="pane-head">Platform</div>
        <div className="pane-body col" style={{ gap: 10, padding: 10, overflow: "auto" }}>
          {status.data ? (
            <>
              <div className="card">
                <h4>Authentication</h4>
                <div className="kv">
                  <span>mode</span>
                  <span>{status.data.auth_mode}</span>
                  <span>tenant</span>
                  <span className="mono">{status.data.tenant}</span>
                  <span>roles</span>
                  <span>{me.data.roles.join(", ")}</span>
                </div>
              </div>
              <div className="card">
                <h4>Metadata database</h4>
                <div className="kv">
                  <span>dialect</span>
                  <span>{status.data.database.dialect}</span>
                  <span>row level security</span>
                  <span>{status.data.database.rls ? "enabled (PostgreSQL)" : "application scoping (SQLite)"}</span>
                  <span>reachable</span>
                  <span className={status.data.database.ok ? "sev-ok" : "sev-error"}>{status.data.database.ok ? "yes" : "no"}</span>
                </div>
              </div>
              <div className="card">
                <h4>Jobs &amp; queue</h4>
                <div className="kv">
                  <span>backend</span>
                  <span>{status.data.job_backend}</span>
                  <span>redis</span>
                  <span>{status.data.redis.configured ? (status.data.redis.ok ? "connected" : "unreachable") : "not configured"}</span>
                  <span>blob store</span>
                  <span>
                    {status.data.blob.kind} {status.data.blob.container ?? status.data.blob.root ?? ""}
                  </span>
                </div>
              </div>
              <div className="card">
                <h4>Deliveries (this tenant)</h4>
                <div className="kv">
                  {Object.entries(status.data.deliveries).map(([k, v]) => (
                    <DeliveryCount key={k} k={k} v={v} />
                  ))}
                  {!Object.keys(status.data.deliveries).length ? <span className="dim">none yet</span> : null}
                </div>
              </div>
            </>
          ) : (
            <Spinner label="loading status" />
          )}
        </div>
      </div>
    </Split>
  );
}

function DeliveryCount({ k, v }: { k: string; v: number }) {
  return (
    <>
      <span>{k}</span>
      <span className={k === "dead" && v ? "sev-error" : ""}>{v}</span>
    </>
  );
}

function summarise(detail: Record<string, unknown>): string {
  return Object.entries(detail)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
    .join(" ");
}
